import { useEffect, useRef, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X, ChevronLeft, ChevronRight, MapPin, Trash2, MoreHorizontal, BookOpen, Check, MessageCircle, Download, Pencil } from "lucide-react";
import { SignedImg } from "@/components/shared/SignedImg";
import { resolveImg } from "@/lib/api";
import { usePhotoGestures, EdgeGlows } from "@/components/shared/usePhotoGestures";
import { ask } from "@/lib/confirm";

// Celoobrazovkový prohlížeč fotek: listování prstem (i šipkami), přiblížení
// dvěma prsty / dvojklepem (175, usePhotoGestures) a špendlíky.
// Vlastní Radix dialog, ať funguje i nad jiným otevřeným oknem (detail úkolu,
// Kontrola směny) — vnořené dialogy Radix zvládá, obyčejný portál by blokoval.
//
// photos: [url]          fixedPins: už uložené závady (jen ke čtení)
// drafts/setDrafts: nové špendlíky {photo_url, x, y, text} — jen když `editable`.
// 185 — jednotný prohlížeč (styl A2): pod fotkou kdo / kdy / odkud (`info`) a ikona
// deníku (`diary`), vše ostatní ve ⋯ (deník, špendlík, WhatsApp, stáhnout, popis, smazat).
//   info(url) → { author, time, src } | null
//   diary     → { isIn(url), toggle(url) } | null
//   onShare(url) / onEditCaption(url) / onDelete(url) — položky ⋯ se ukážou jen když jsou
//   review    → { onDecide(url, approved), onFinish() } — 186: kontrola fotek do deníku,
//               dole Nechci / Do deníku a po rozhodnutí hned další fotka
const initials = (name) => (name || "").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";
const SRC = { attendance: "docházka", daysheet: "denní výkaz", task: "úkol", upload: "nahráno ručně" };
const hhmm = (iso) => { try { return new Intl.DateTimeFormat("cs-CZ", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Prague" }).format(new Date(iso)); } catch { return ""; } };
// Popisek pod fotkou z řádku banky fotek (diary_photos): kdo, kdy, odkud. U fotky
// z úkolu i název úkolu — v deníku je tak hned vidět, ke kterému úkolu patří.
export function photoInfo(p) {
  if (!p) return null;
  const src = p.source === "task" ? `úkol${p.caption ? `: ${p.caption}` : ""}` : (p.source_label || SRC[p.source] || "docházka").toLowerCase();
  return { author: p.author_name, time: p.created_at ? hhmm(p.created_at) : "", src };
}
async function downloadPhoto(url) {
  try {
    const r = await fetch(resolveImg(url));
    const blob = await r.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = (url.split("/").pop() || "fotka").split("?")[0];
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 4000);
  } catch { window.open(resolveImg(url), "_blank"); }
}
const TONE = { open: "#e0703f", fixed: "#d4930d", accepted: "#1f8a5b", draft: "#e0703f" };

export function PhotoPinViewer({ open, onClose, photos = [], start = 0, fixedPins = [], drafts = [], setDrafts,
  editable = false, title, focusDraft = null, onDone, info, diary, onShare, onEditCaption, onDelete, review }) {
  const [i, setI] = useState(start);
  const [menu, setMenu] = useState(false);
  const [pinMode, setPinMode] = useState(false);
  const [sel, setSel] = useState(null);           // index vybraného nového špendlíku
  const stage = useRef(null);
  const track = useRef(null);
  const inputRef = useRef(null);
  const n = photos.length;
  const base = fixedPins.length;                   // nové špendlíky číslujeme za uloženými

  useEffect(() => {
    if (!open) return;
    setI(Math.min(Math.max(start, 0), Math.max(n - 1, 0)));
    setPinMode(editable && focusDraft != null);
    setSel(focusDraft);
    setMenu(false);
  }, [open, start, focusDraft, editable, n]);

  const go = (k) => {
    // Na krajích se nepřeskakuje dokola — jen se rozsvítí okraj (178).
    if ((k < 0 && i <= 0) || (k > 0 && i >= n - 1)) { flashRef.current?.(k < 0 ? "left" : "right"); return; }
    setI((v) => Math.min(n - 1, Math.max(0, v + k))); setSel(null);
  };
  const flashRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const on = (e) => {
      if (e.target?.tagName === "INPUT") return;
      if (e.key === "ArrowLeft") go(-1);
      if (e.key === "ArrowRight") go(1);
      if (review && photos[i] && !(editable && pinMode)) {
        if (e.key === "d" || e.key === "D") decide(true);
        if (e.key === "n" || e.key === "N") decide(false);
        return;
      }
      if ((e.key === "d" || e.key === "D") && diary && photos[i]) diary.toggle(photos[i]);
    };
    window.addEventListener("keydown", on);
    return () => window.removeEventListener("keydown", on);
  }); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (sel != null) setTimeout(() => inputRef.current?.focus(), 40); }, [sel]);

  const tapAt = (e) => {
    if (!(editable && pinMode)) return;
    const box = document.querySelector(`[data-pin-frame="${i}"]`);
    if (!box) return;
    const r = box.getBoundingClientRect();
    if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) return;
    const x = +(((e.clientX - r.left) / r.width) * 100).toFixed(2);
    const y = +(((e.clientY - r.top) / r.height) * 100).toFixed(2);
    const url = photos[i];
    const hit = drafts.findIndex((d) => d.photo_url === url
      && Math.hypot((d.x / 100) * r.width - (e.clientX - r.left), (d.y / 100) * r.height - (e.clientY - r.top)) < 24);
    if (hit >= 0) { setSel(hit); return; }
    if (sel != null && drafts[sel] && !drafts[sel].text.trim()) {
      // Nepopsaný špendlík se jen přesune — netrefil se, nechce další.
      setDrafts((ds) => ds.map((d, k) => (k === sel ? { ...d, photo_url: url, x, y } : d)));
      return;
    }
    setDrafts((ds) => [...ds, { photo_url: url, x, y, text: "" }]);
    setSel(drafts.length);
  };

  const pinning = editable && pinMode;
  const { zoomed, handlers, trackProps, glowRefs, flashEdge } = usePhotoGestures({
    stageRef: stage, trackRef: track, index: i, canPrev: i > 0, canNext: i < n - 1, onStep: go,
    getZoomEl: () => track.current?.querySelector(`[data-pin-frame="${i}"]`),
    onTap: pinning ? tapAt : undefined, tapZoom: !pinning,
    onClose: pinning ? undefined : onClose, resetKey: open,
  });

  flashRef.current = flashEdge;
  const count = (url) => fixedPins.filter((p) => p.photo_url === url).length + drafts.filter((p) => p.photo_url === url).length;
  // Popis patří jen k fotce, na které špendlík je (178) — jinde se okénko neukáže.
  const selDraft = sel != null && drafts[sel] && drafts[sel].photo_url === photos[i] ? drafts[sel] : null;
  const [hint, setHint] = useState("");
  // Hotovo: každá závada musí mít popis; pak se okno zavře a nadřazený dialog
  // rovnou nabídne vytvoření úkolu (onDone).
  const finish = () => {
    if (!drafts.length) { setPinMode(false); setSel(null); return; }
    const k = drafts.findIndex((d) => !d.text.trim());
    if (k >= 0) {
      const at = photos.indexOf(drafts[k].photo_url);
      if (at >= 0) setI(at);
      setSel(k); setHint("Napište, co je u této závady špatně.");
      return;
    }
    setPinMode(false); setSel(null); setHint("");
    if (onDone) onDone(); else onClose?.();
  };
  const cur = photos[i];
  // 186: kontrola — rozhodnutí a hned další fotka; po poslední konec kontroly.
  const decide = (approved) => {
    if (!review || !photos[i]) return;
    review.onDecide(photos[i], approved);
    setSel(null); setPinMode(false);
    if (i < n - 1) setI(i + 1); else review.onFinish?.();
  };
  const meta = cur && info ? info(cur, i) : null;
  const inDiary = !!(cur && diary?.isIn(cur));
  const menuItems = [
    diary && cur && { key: "diary", label: inDiary ? "Odebrat z deníku" : "Přidat do deníku", icon: BookOpen, run: () => diary.toggle(cur) },
    editable && { key: "pin", label: "Přidat špendlík", icon: MapPin, run: () => setPinMode(true) },
    onShare && cur && { key: "share", label: "Sdílet na WhatsApp", icon: MessageCircle, run: () => onShare(cur) },
    cur && { key: "download", label: "Stáhnout", icon: Download, run: () => downloadPhoto(cur) },
    onEditCaption && cur && { key: "caption", label: "Upravit popis", icon: Pencil, run: () => onEditCaption(cur) },
    onDelete && cur && { key: "delete", label: "Smazat fotku", icon: Trash2, danger: true,
      run: async () => { if (await ask("Opravdu smazat tuto fotku? Zmizí z banky i z deníku.")) onDelete(cur); } },
  ].filter(Boolean);
  const pinEl = (p, num, tone, active, key) => (
    <span key={key} className="absolute -ml-[15px] -mt-[15px] w-[30px] h-[30px] rounded-full text-white text-[13px] font-extrabold grid place-items-center border-[2.5px] border-white pointer-events-none"
      style={{ left: `${p.x}%`, top: `${p.y}%`, background: TONE[tone] || TONE.open, transform: "scale(var(--pz, 1))",
        boxShadow: active ? `0 0 0 5px ${TONE.open}66, 0 2px 8px rgba(0,0,0,.35)` : "0 2px 8px rgba(0,0,0,.35)" }}>{num}</span>
  );

  return (
    <DialogPrimitive.Root open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[70] bg-black" />
        <DialogPrimitive.Content aria-describedby={undefined} onOpenAutoFocus={(e) => e.preventDefault()}
          className="fixed inset-0 z-[70] flex flex-col bg-[#0b0b0d] text-white focus:outline-none select-none" data-testid="photo-pin-viewer">
          <DialogPrimitive.Title className="sr-only">{title || "Fotky"}</DialogPrimitive.Title>
          <div className="flex items-center justify-between gap-2 px-3 pb-2 pt-[max(0.75rem,env(safe-area-inset-top))]">
            <button type="button" onClick={onClose} aria-label="Zavřít" data-testid="pin-viewer-close"
              className="w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 grid place-items-center"><X size={22} /></button>
            <div className="text-center min-w-0">
              <div className="font-bold text-sm tabular-nums">{n ? (review ? `Ke schválení · ${i + 1} / ${n}` : `${i + 1} / ${n}`) : ""}</div>
              {title && <div className="text-xs text-white/60 truncate">{title}</div>}
            </div>
            {menuItems.length ? (
              <button type="button" onClick={() => setMenu(true)} aria-label="Další možnosti" data-testid="pin-viewer-menu-btn"
                className="w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 grid place-items-center"><MoreHorizontal size={22} /></button>
            ) : <span className="w-11" />}
          </div>

          <div {...handlers} data-testid="pin-viewer-stage"
            className={`relative flex-1 overflow-hidden ${pinning ? "cursor-crosshair" : zoomed ? "cursor-grab" : ""}`}>
            <EdgeGlows glowRefs={glowRefs} />
            <div {...trackProps} className="absolute inset-0 flex will-change-transform">
              {photos.map((url, k) => (
                <div key={k} className="shrink-0 w-full h-full flex items-center justify-center px-1.5">
                  <span data-pin-frame={k} data-zoom
                    className={`relative inline-block max-w-full origin-center ${pinning && k === i ? "outline outline-[3px] outline-brand rounded-[6px]" : ""}`}>
                    {Math.abs(k - i) <= 2 && (
                      <SignedImg url={url} alt="" className="block max-w-full max-h-[calc(100dvh-260px)] object-contain rounded-[6px] pointer-events-none" />
                    )}
                    {fixedPins.filter((p) => p.photo_url === url).map((p) => pinEl(p, p.n, p.status, false, `f${p.id || p.n}`))}
                    {drafts.map((p, d) => (p.photo_url === url ? pinEl(p, base + d + 1, "draft", d === sel, `d${d}`) : null))}
                  </span>
                </div>
              ))}
            </div>
            {i > 0 && (
              <button type="button" onClick={() => go(-1)} aria-label="Předchozí" data-testid="pin-viewer-prev"
                className="hidden md:grid absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/45 hover:bg-black/60 place-items-center"><ChevronLeft size={24} /></button>
            )}
            {i < n - 1 && (
              <button type="button" onClick={() => go(1)} aria-label="Další" data-testid="pin-viewer-next"
                className="hidden md:grid absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/45 hover:bg-black/60 place-items-center"><ChevronRight size={24} /></button>
            )}
          </div>

          {(meta || diary) && (
            <div className="flex items-center gap-3 px-4 pt-3 w-full max-w-xl mx-auto" data-testid="pin-viewer-info">
              {meta && (
                <>
                  <span className="w-10 h-10 rounded-full bg-white/15 grid place-items-center text-[13px] font-bold shrink-0">{initials(meta.author)}</span>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-[15px] truncate">{meta.author || "—"}</div>
                    <div className="text-[12.5px] text-white/60 truncate">{[meta.time, meta.src].filter(Boolean).join(" · ")}</div>
                  </div>
                </>
              )}
              {!meta && <span className="flex-1" />}
              {diary && (
                <button type="button" onClick={() => diary.toggle(photos[i])} data-testid="pin-viewer-diary"
                  aria-label={inDiary ? "Fotka je v deníku – klepnutím odebrat" : "Fotka není v deníku – klepnutím přidat"} title={inDiary ? "V deníku" : "Není v deníku"}
                  className={`relative w-12 h-12 rounded-full grid place-items-center shrink-0 transition-colors ${inDiary ? "bg-success text-white" : "border-2 border-white/25 text-white/60"}`}>
                  <BookOpen size={21} />
                  {inDiary && <span className="absolute -right-0.5 -bottom-0.5 w-5 h-5 rounded-full bg-white grid place-items-center"><Check size={12} strokeWidth={4} className="text-success" /></span>}
                </button>
              )}
            </div>
          )}

          {n > 1 && n <= 24 && (
            <div className="flex gap-1.5 justify-center px-3 pt-3" data-testid="pin-viewer-dots">
              {photos.map((url, k) => (
                <button key={k} type="button" onClick={() => { setI(k); setSel(null); }} aria-label={`Fotka ${k + 1}`} aria-current={k === i}
                  className={`h-2 rounded-full transition-all motion-reduce:transition-none ${k === i ? "w-6 bg-white" : diary?.isIn(url) ? "w-2 bg-success" : count(url) > 0 ? "w-2 bg-brand" : "w-2 bg-white/25"}`} />
              ))}
            </div>
          )}

          <div className="px-3.5 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))] flex flex-col gap-2 w-full max-w-xl mx-auto">
            {editable && pinMode && selDraft && (
              <div className="flex items-center gap-2 rounded-[16px] bg-white/10 p-1.5 pl-2" data-testid="pin-note">
                <span className="w-7 h-7 rounded-full bg-brand grid place-items-center text-[13px] font-extrabold shrink-0">{base + sel + 1}</span>
                <input ref={inputRef} value={selDraft.text} enterKeyHint="done" placeholder="Co je tady špatně?" aria-label="Popis závady"
                  onChange={(e) => setDrafts((ds) => ds.map((d, k) => (k === sel ? { ...d, text: e.target.value } : d)))}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); setSel(null); } }}
                  className="flex-1 min-w-0 h-11 rounded-[12px] bg-white text-[#111] px-3 text-[16px] outline-none" data-testid="pin-text" />
                <button type="button" onClick={() => { setDrafts((ds) => ds.filter((_, k) => k !== sel)); setSel(null); }} aria-label="Smazat špendlík"
                  className="w-11 h-11 rounded-[12px] bg-white/15 grid place-items-center shrink-0" data-testid="pin-delete"><Trash2 size={17} /></button>
              </div>
            )}
            <p className="text-center text-[13px] text-white/75 min-h-[20px]">
              {editable && pinMode
                ? (hint && selDraft && !selDraft.text.trim() ? hint : selDraft ? (selDraft.text.trim() ? "Klepnutím jinam přidáte další závadu." : "Napište, co je špatně. Klepnutím jinam špendlík přesunete.") : "Klepněte na místo problému.")
                : zoomed ? "Dvojklepem přiblížení zrušíte."
                : n > 1 ? "Přejeďte do strany pro další fotku · dvěma prsty přiblížíte." : "Dvěma prsty nebo dvojklepem přiblížíte."}
            </p>
            {review && !(editable && pinMode) && (
              <div className="grid grid-cols-2 gap-2" data-testid="review-buttons">
                <button type="button" onClick={() => decide(false)} data-testid="review-reject"
                  className="h-16 rounded-[20px] bg-white/10 hover:bg-white/15 font-bold text-[16px] flex items-center justify-center gap-2"><X size={22} /> Nechci</button>
                <button type="button" onClick={() => decide(true)} data-testid="review-approve"
                  className="h-16 rounded-[20px] bg-success hover:bg-success/90 font-extrabold text-[16px] flex items-center justify-center gap-2"><Check size={22} strokeWidth={3} /> Do deníku</button>
              </div>
            )}
            {editable && review && !pinMode && (
              <button type="button" onClick={() => setPinMode(true)} data-testid="pin-mode-btn"
                className="h-11 rounded-full font-bold text-[15px] text-[#ff9a6a] flex items-center justify-center gap-2"><MapPin size={18} /> Označit špendlíkem</button>
            )}
            {editable && !(review && !pinMode) && (
              <button type="button" onClick={() => { if (pinMode) finish(); else setPinMode(true); }}
                data-testid="pin-mode-btn"
                className={`h-[54px] rounded-full font-extrabold text-[15.5px] flex items-center justify-center gap-2 ${pinMode ? "bg-white text-[#111]" : "bg-brand text-white"}`}>
                {pinMode ? `Hotovo · ${drafts.length} ${drafts.length === 1 ? "závada" : drafts.length >= 2 && drafts.length <= 4 ? "závady" : "závad"}` : <><MapPin size={18} /> Označit špendlíkem</>}
              </button>
            )}
          </div>

          {menu && (
            <>
              <button type="button" aria-label="Zavřít nabídku" onClick={() => setMenu(false)} className="absolute inset-0 bg-black/60 cursor-default" />
              <div className="absolute left-2 right-2 bottom-[max(0.5rem,env(safe-area-inset-bottom))] md:left-auto md:bottom-auto md:right-3 md:top-[calc(max(0.75rem,env(safe-area-inset-top))+3.25rem)] md:w-72 flex flex-col gap-2" data-testid="pin-viewer-menu">
                <div className="rounded-[22px] bg-[#1c1c1e] overflow-hidden md:p-1.5 md:shadow-2xl">
                  {menuItems.map((m) => (
                    <button key={m.key} type="button" onClick={() => { setMenu(false); m.run(); }} data-testid={`pin-viewer-menu-${m.key}`}
                      className={`w-full h-14 md:h-11 md:rounded-[12px] flex items-center gap-3.5 px-4 md:px-3 text-[16px] md:text-[14.5px] text-left border-b border-white/10 last:border-0 md:border-0 hover:bg-white/5 ${m.danger ? "text-[#ff6b6b]" : "text-white"}`}>
                      <span className={`w-9 h-9 md:w-7 md:h-7 rounded-full grid place-items-center shrink-0 ${m.danger ? "bg-[#ff6b6b]/15" : "bg-white/10"}`}><m.icon size={17} /></span>
                      {m.label}
                    </button>
                  ))}
                </div>
                <button type="button" onClick={() => setMenu(false)} className="md:hidden h-14 rounded-[22px] bg-[#1c1c1e] font-bold text-[16px]">Zrušit</button>
              </div>
            </>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
